import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import { useI18n } from "../context/I18nContext";
import { useToast } from "../components/Toast";

const CUISINES = ["Turkish", "Italian", "Mexican", "Japanese", "Indian", "Chinese", "French", "Greek", "Russian", "Spanish", "Korean", "Thai", "American", "Mediterranean", "Middle Eastern", "Global"];

function parseMeasuredIngredients(text) {
  const lines = String(text || "").split(/\n+/).map((item) => item.trim()).filter(Boolean);
  const rows = lines.map((line) => {
    const match = line.match(/^(.*?)[,\s]+(\d+(?:[.,]\d+)?)\s*(?:g|gr|gram)?$/i);
    if (!match) return null;
    return { name: match[1].replace(/[,;-]+$/g, "").trim(), grams: Number(match[2].replace(",", ".")) };
  });
  return rows.length && rows.every((item) => item?.name && item.grams > 0) ? rows : null;
}

export default function AddRecipe({ navigate }) {
  const { state, actions } = useApp();
  const { language, t } = useI18n();
  const { toast } = useToast();
  const [form, setForm] = useState({
    title: "",
    cuisine: "Global",
    mealType: "dinner",
    category: "balanced",
    calories: "",
    prepTime: 15,
    cookTime: 20,
    servings: 2,
    youtubeUrl: "",
    ingredientsText: "",
    stepsText: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submissionTermsAccepted, setSubmissionTermsAccepted] = useState(false);
  const [copyrightDeclared, setCopyrightDeclared] = useState(false);

  const update = (key, value) => setForm((previous) => ({ ...previous, [key]: value }));

  const submit = async (event) => {
    event.preventDefault();
    if (!state.user) {
      toast(t("loginRequiredRecipe"), "error");
      return;
    }
    if (!submissionTermsAccepted || !copyrightDeclared) {
      toast(t("recipeSubmissionTermsRequired"), "error");
      return;
    }
    const nutritionIngredients = parseMeasuredIngredients(form.ingredientsText);
    if (!nutritionIngredients) {
      toast(t("recipeIngredientGramRequired"), "error");
      return;
    }

    setSaving(true);
    try {
      const uploadedImage = imageFile ? await actions.uploadRecipeImage(imageFile) : "";
      await actions.createRecipe({
        ...form,
        sourceLanguage: language,
        image: uploadedImage,
        calories: Number(form.calories || 0),
        prepTime: Number(form.prepTime),
        cookTime: Number(form.cookTime),
        servings: Number(form.servings),
        ingredients: nutritionIngredients.map((item) => item.name),
        nutritionIngredients,
        steps: form.stepsText.split(/\n/).map((item) => item.trim()).filter(Boolean),
        submissionTermsAccepted,
        copyrightDeclared,
      });
      toast(t("recipePendingReview"), "success");
      navigate("meals");
    } catch (error) {
      toast(error.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>➕ {t("addOwnRecipe")}</h1>
        <p>{t("addRecipeIntro")}</p>
        {!state.user && <p className="muted">{t("addRecipeLoginNote")}</p>}
      </div>

      <form className="recipe-form" onSubmit={submit}>
        <label>
          {t("recipeName")}
          <input value={form.title} onChange={(event) => update("title", event.target.value)} placeholder={t("recipeNamePlaceholder")} required />
        </label>

        <div className="form-grid">
          <label>{t("cuisine")}
            <select value={form.cuisine} onChange={(event) => update("cuisine", event.target.value)}>
              {CUISINES.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </label>
          <label>{t("mealType")}
            <select value={form.mealType} onChange={(event) => update("mealType", event.target.value)}>
              <option value="breakfast">{t("breakfast")}</option>
              <option value="lunch">{t("lunch")}</option>
              <option value="dinner">{t("dinner")}</option>
              <option value="dessert">{t("dessert")}</option>
            </select>
          </label>
          <label>{t("category")}
            <select value={form.category} onChange={(event) => update("category", event.target.value)}>
              <option value="healthy">{t("healthy")}</option>
              <option value="tasty">{t("tasty")}</option>
              <option value="balanced">{t("balanced")}</option>
            </select>
          </label>
          <label>{t("caloriesOptional")}
            <input type="number" min="1" value={form.calories} onChange={(event) => update("calories", event.target.value)} placeholder={t("caloriesAiPlaceholder")} />
            <span className="field-hint">{t("caloriesAiHint")}</span>
          </label>
          <label>{t("prepMinutes")}
            <input type="number" min="0" value={form.prepTime} onChange={(event) => update("prepTime", event.target.value)} />
          </label>
          <label>{t("cookMinutes")}
            <input type="number" min="0" value={form.cookTime} onChange={(event) => update("cookTime", event.target.value)} />
          </label>
          <label>{t("servings")}
            <input type="number" min="1" value={form.servings} onChange={(event) => update("servings", event.target.value)} />
          </label>
        </div>

        <label>
          {t("youtubeUrl")}
          <input
            type="url"
            value={form.youtubeUrl}
            onChange={(event) => update("youtubeUrl", event.target.value)}
            placeholder={t("youtubeUrlPlaceholder")}
          />
        </label>

        <label>
          {t("recipeImage")}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(event) => setImageFile(event.target.files?.[0] || null)}
          />
          <span className="field-hint">{t("recipeImageHint")}</span>
        </label>

        <label>
          {t("ingredients")}
          <textarea value={form.ingredientsText} onChange={(event) => update("ingredientsText", event.target.value)} placeholder={t("ingredientsPlaceholder")} required />
          <span className="field-hint">{t("recipeIngredientGramHint")}</span>
        </label>

        <label>
          {t("steps")}
          <textarea value={form.stepsText} onChange={(event) => update("stepsText", event.target.value)} placeholder={t("stepsPlaceholder")} required />
        </label>

        <section className="recipe-consent">
          <strong>{t("recipeSubmissionTermsTitle")}</strong>
          <span className="field-hint">{t("recipeSubmissionTermsHint")}</span>
          <label className="recipe-consent-row">
            <input type="checkbox" checked={submissionTermsAccepted} onChange={(event) => setSubmissionTermsAccepted(event.target.checked)} />
            <span>{t("recipeSubmissionTermsAccept")}</span>
          </label>
          <label className="recipe-consent-row">
            <input type="checkbox" checked={copyrightDeclared} onChange={(event) => setCopyrightDeclared(event.target.checked)} />
            <span>{t("recipeCopyrightDeclarationAccept")}</span>
          </label>
        </section>

        <button className="btn btn-primary" type="submit" disabled={saving || !state.user}>
          {saving ? t("saving") : t("saveRecipe")}
        </button>
      </form>
    </div>
  );
}
